/**
 * ItemSelectorResponsive Plugin
 * Enable the nestedItemSelector setting to be actually responsive.
 * Normally, the setting is applied at pageload only,
 * so we implement that behaviour on breakpoint changes, too.
 */

import $ from 'jquery';


/**
 * Creates the item selector responsive plugin.
 * @class The Item Selector Responsive Plugin
 * @param {Owl} carousel - The Owl Carousel
 */
var ItemSelectorResponsive = function(carousel) {
	/**
	 * Reference to the core.
	 * @protected
	 * @type {Owl}
	 */
	this._core = carousel;

	if(this.hasMultipleResponsiveSelectors()) {
		// There are selectors in responsive settings, so actually init our plugin
		this.init();
	}
};

/**
 * Init the plugin
 * @protected
 */
ItemSelectorResponsive.prototype.init = function() {
	// Copy the html of the content for later
	this.content = this._core.$element.html().trim();

	// Remove all content
	// Owl leaves artifacts of the unused initial content in the DOM when using nestedItemSelector.
	// We handle the contents via replace.owl.carousel (replaceContent) to fix this.
	this._core.$element.empty();

	// Get the initial selector that is used on pageload and replace the content
	this._core.$element.one("initialized.owl.carousel", (evt) => {
		// Readd the initial content to owl carousel
		window.setTimeout(() => {
			this.replaceContent();
		}, 0);

	});

	// Wait for breakpoint changes by listening for a Foundation breakpoint event
	$(window).on('changed.zf.mediaquery.responsive-selector', (evt, newSize, oldSize) => {
		if(newSize === oldSize) {
			return;
		}

		const newIndex = this.getRefreshedIndex();
		this.replaceContent(newIndex);
	});
};

/**
 * Check the responsive options for nestedItemSelector settings.
 * We want to know if there are two or more distinct item selectors within the responsive setting.
 * @protected
 * @returns {boolean}
 */
ItemSelectorResponsive.prototype.hasMultipleResponsiveSelectors = function() {
	if(!this._core.options.responsive) {
		return false;
	}

	const responsive = this._core.options.responsive;
	let values = [];

	// Add the default value if there is no setting for the 0px breakpoint
	if(typeof responsive["0"] === undefined) {
		values.push(false);
	}

	// Add the settings for the defined breakpoints
	for(let prop in responsive) {
		if( responsive.hasOwnProperty( prop ) ) {
			const value = responsive[prop].nestedItemSelector;
			if(typeof value !== "undefined" && values.indexOf(value) === -1) {
					values.push(value);
			}
		}
	}
	// Do we have more than one selector values?
	return (values.length > 1);
};

/**
 * Readd the original content to owl carousel to apply the current nestedItemSelector
 * @param {integer} index - The new index for the carousel
 * @protected
 */
ItemSelectorResponsive.prototype.replaceContent = function(index) {
	this._core.$element.trigger('replace.owl.carousel', this.content);
	this._core.$element.trigger('refresh.owl.carousel');

	if(typeof index === "undefined" || index === this._core._current) {
		// This is kind of a hacky workaround to get owl.lazyload to load images again
		this._core.trigger('change', { property: { name: 'position', value: this._core._current } });
	} else {
		this._core.$element.trigger("to.owl.carousel", [index, 0]);
	}
};

/**
 * Actually handle nestedItemSelector settings by destroying and reading the owl carousel
 * @protected
 * @returns {integer}
 */
ItemSelectorResponsive.prototype.getRefreshedIndex = function() {
	const oldBreakpoint = this._core._breakpoint;
	const oldSelector = this._core.options.responsive[oldBreakpoint].nestedItemSelector || false;

	// Refresh the carousel to get up to date values
	this._core.$element.trigger('refresh.owl.carousel');

	const newBreakpoint = this._core._breakpoint;
	const newSelector = this._core.options.responsive[newBreakpoint].nestedItemSelector || false;

	if(newSelector === oldSelector) {
		// The selector didn't change, nothing to do here
		return this._core._current;
	}

	// Save a jQuery object version of our content that we use to derive the new from the old index
	if(typeof this.$content === "undefined") {
		this.$content = $(this.content).filter(function() {
			// Filter out text nodes as they violate our calculations
			return this.nodeType !== 3;
		});
	}

	let oldIndex = this._core._current,
		oldElem,
		newIndex,
		newElems;

	// Find the old current child in $content first
	if(oldSelector) {
		oldElem = this.$content.find("." + oldSelector).eq(oldIndex);
	} else {
		oldElem = this.$content.eq(oldIndex);
	}

	// Find all new children (they could be nested)
	if(newSelector) {
		newElems = this.$content.find("." + newSelector);
	} else {
		newElems = this.$content;
	}

	if(newElems.is(oldElem)) {
		newIndex = this._core._current;
	} else {
		// The new index element could be inside of the old index element or the other way round
		newIndex = newElems.has(oldElem).index();
		newIndex = newIndex > -1 ? newIndex : newElems.index( oldElem.find("." + newSelector) );
		newIndex = newIndex > -1 ? newIndex : 0;
	}

	return newIndex;
};

/**
 * Destroys the plugin.
 * @protected
 */
ItemSelectorResponsive.prototype.destroy = function() {
	$(window).off('changed.zf.mediaquery.responsive-selector');
};

$.fn.owlCarousel.Constructor.Plugins.ItemSelectorResponsive = ItemSelectorResponsive;